import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';

import { UiCheckboxTypes } from 'src/components/UI/checkbox/types';
import { getInputError } from 'src/components/UI/inputs/common';
import { uiMemoFormPropsInputCompareFn } from 'src/components/UI/UIMemoFormPropsCompareFn';
import { useIdOrRandomString } from 'src/utils/formHelper';
import { inputBaseHook } from 'src/utils/hooks/inputs';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { useHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { SafeAnyType } from 'src/utils/safeAny';
import { getTestId } from 'src/utils/testing';
import { MaterialErrorMessage } from 'src/components/UI/common/MaterialErrorMessage';

export const UiCheckbox: React.FC<UiCheckboxTypes> = memo(
  ({
    formProps: {
      register,
      clearErrors,
      getValues,
      formState: { errors },
    },
    name,
    label,
    id: propId,
    disabled,
    required = false,
    className,
    addAfter,
    wrapperClassname,
    labelClassName,
    clean,
    checked,
    onChange,
    defaultChecked,
    dataTestId,
    validate,
    showHoverError,
    onClickWrapper,
    material,
    validationName,
  }: UiCheckboxTypes) => {
    const { t } = useTranslation();

    const mountRef = useRef(false);

    const [localChecked, setLocalChecked] = useState<boolean>(() => getValues(name) || false);

    useEffect(() => inputBaseHook(mountRef, name, clearErrors, validationName), [clearErrors, name, validationName]);

    const reg = register(name, {
      validate: (v: SafeAnyType) => {
        if (mountRef.current) {
          if (required && !v) {
            return t('common.validation.required', { label: name }).toString();
          }

          if (isFunction(validate)) {
            return validate(v as string);
          }
        }

        return undefined;
      },
    });

    const error = getInputError(errors, name, validationName);

    const { hoverMessage, handleChangeInputHover } = useHoverErrors({
      name,
      clearErrors,
      error,
      showAnyError: showHoverError,
      validationName,
    });

    const id = useIdOrRandomString(propId);

    const onChangeLocal = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!disabled) {
          reg.onChange(e);

          handleChangeInputHover();

          setLocalChecked(e.target.checked);

          if (isFunction(onChange)) {
            onChange(name, e.target.checked);
          }
        }
      },
      [disabled, handleChangeInputHover, name, onChange, reg] // be aware of onChange
    );

    const [inputTestId, labelTestId]: [string | undefined, string | undefined] = useMemo(() => {
      if (!dataTestId) {
        return [undefined, undefined];
      }
      const testId = getTestId('checkbox', dataTestId, name);
      return [testId(), testId('label')];
    }, [dataTestId, name]);

    const component = (
      <>
        <input
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...reg}
          type="checkbox"
          className={classNames(className, {
            disabled,
            'filled-in': !clean,
            'material-disabled': material && disabled,
          })}
          defaultChecked={defaultChecked}
          onChange={onChangeLocal}
          checked={isNil(checked) ? localChecked : checked}
          id={id}
          data-testid={inputTestId}
        />
        <label
          className={classNames(labelClassName, {
            error: !!error,
          })}
          htmlFor={id}
          data-testid={labelTestId}
        >
          {label}
        </label>
        {addAfter}
        {material && <MaterialErrorMessage error={hoverMessage} />}
      </>
    );

    return (
      <UiHoverTooltip
        text={hoverMessage}
        id={name}
        render={!!hoverMessage && !material}
        tooltipClassName="error-tooltip"
        noWrapper
      >
        {clean ? (
          <div role="presentation" onClick={onClickWrapper}>
            {component}
          </div>
        ) : (
          <p
            role="presentation"
            className={classNames(material ? 'checkbox-material custom' : 'checkbox', wrapperClassname, {
              disabled,
              withAddAfter: !!addAfter,
            })}
            onClick={onClickWrapper}
          >
            {component}
          </p>
        )}
      </UiHoverTooltip>
    );
  },
  uiMemoFormPropsInputCompareFn
);
